import { DateTime } from 'luxon'
import { formatTimeOrMonthDay } from '@/utils/dateHelpers'
import { RESOLVED_CASE_STATUS } from './constants'
import type {
  ConversationSummary,
  CustomerCaseSummary,
  OperatorIdentity,
} from './types'

export interface RailRow {
  id: number
  title: string
  /** Shown as an avatar when an operator spoke last. */
  operator: OperatorIdentity | null
  /** An open case Heard still holds; shown as the waiting clock when there is no avatar. */
  waiting: boolean
  /** Accented first fragment, when the conversation is waiting on the customer. */
  attention: string | null
  /** Remaining fragments, joined with a separator by the caller. */
  details: string[]
}

const YOUR_TURN = 'Your turn'

const operatorName = (operator: OperatorIdentity | null): string =>
  operator?.firstName || operator?.friendlyName || 'Heard'

const waitingLabel = (customerCase: CustomerCaseSummary): string =>
  customerCase.owner
    ? `Waiting on ${operatorName(customerCase.owner)}`
    : 'Waiting on Heard'

const lastOperator = (
  customerCase: CustomerCaseSummary | null | undefined
): OperatorIdentity | null =>
  customerCase?.lastHumanTurn?.kind === 'operator'
    ? customerCase.lastHumanTurn.operator
    : null

const activityAt = (conversation: ConversationSummary): string =>
  conversation.lastActivity?.at ??
  conversation.lastVisibleReplyAt ??
  conversation.updatedAt

export const toRailRow = (
  conversation: ConversationSummary,
  now: DateTime = DateTime.now()
): RailRow => {
  const customerCase = conversation.case ?? null
  const replier = lastOperator(customerCase)
  const time = formatTimeOrMonthDay(activityAt(conversation), now)
  const row = {
    id: conversation.id,
    title: conversation.title || 'Untitled conversation',
    operator: replier,
  }

  if (!customerCase) {
    return { ...row, waiting: false, attention: null, details: [time] }
  }

  if (customerCase.returnedToHaven) {
    return {
      ...row,
      waiting: false,
      attention: null,
      details: ['Back with Haven', time],
    }
  }

  if (customerCase.status === RESOLVED_CASE_STATUS) {
    return { ...row, waiting: false, attention: null, details: [time] }
  }

  if (customerCase.waitingOn === 'customer') {
    const details = replier
      ? [`${operatorName(replier)} replied`, time]
      : [time]
    return {
      ...row,
      waiting: false,
      attention: YOUR_TURN,
      details: customerCase.reopened ? ['Reopened', ...details] : details,
    }
  }

  if (customerCase.reopened) {
    return {
      ...row,
      waiting: true,
      attention: null,
      details: ['Reopened', waitingLabel(customerCase)],
    }
  }

  return {
    ...row,
    waiting: true,
    attention: null,
    details: replier
      ? [`${operatorName(replier)} replied`, time]
      : [waitingLabel(customerCase)],
  }
}
